import React, { useCallback, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import SoundChoiceRoundView from './SoundChoiceRoundView';
import LetterHuntRoundView from './LetterHuntRoundView';
import MemoryTrainRoundView from './MemoryTrainRoundView';
import './syllableFinal.css';

export default function MixedFinalRoundView({ round, localProgress, onComplete, onNextRound, ...props }) {
  const [stage, setStage] = useState(0);
  const [helpUsed, setHelpUsed] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const accepted = useRef([]);
  const logs = useRef([]);
  const finished = useRef(false);
  const step = round.items[stage];
  const childRound = useMemo(() => {
    const current = round.items[stage];
    return { ...current, game_id: round.game_id, round_id: round.round_id, student: round.student,
      assessment_mode: current.mechanic, task_type: current.mechanic === 'sound_choice' ? 'heard_sound_choice' : current.mechanic,
      audio: round.audio, intro_audio_id: current.intro_audio_id,
      intro_sequence: current.intro_sequence || (stage === 0 && round.source_level === 4 ? [round.intro_audio_id, current.intro_audio_id] : undefined),
      complete_audio_id: round.complete_audio_id, first_correct_audio_id: 's_l1_g2_pop',
      help_audio_id: round.help_audio_id, wrong_audio_id: round.wrong_audio_id,
      v22: true, local_tour: true, preview: round.preview, final_step: true,
      source_level: round.source_level, unmeasured_help: round.unmeasured_help,
      continuing: true, next_round: null, skip_completion_speech: stage < round.items.length - 1,
      badge_title: round.badge_title || '🏆 Ses Avcısı Finali', progress_text: `${stage + 1} / ${round.items.length}` };
  }, [round, stage]);
  const record = useCallback((gameId, events) => {
    const current = round.items[stage];
    const picks = events.filter((event) => event.kind === 'select').map((event) => ({ kind: 'select', item_id: current.id,
      selected_id: current.mechanic === 'letter_hunt' ? event.item_id : event.selected_id }));
    const helping = events.some((event) => event.kind === 'help_prompt');
    const earlierHelp = accepted.current.some((event) => event.kind === 'help_prompt');
    const all = [...accepted.current, ...picks, ...(helping && !earlierHelp ? [{ kind: 'help_prompt' }] : [])];
    logs.current = all;
    if (helping && !earlierHelp) setHelpUsed(true);
    const result = localProgress(gameId, all);
    const stones = current.stones || current.items?.[0]?.stones;
    const completed = picks.some((pick) => (stones && current.physical_stones
      ? pick.selected_id.split(',').map((id) => stones.find((stone) => stone.id === id)?.letter || '').join('')
      : pick.selected_id) === current.expected_id);
    if (completed && stage === round.items.length - 1) setCelebrating(true);
    return { ...result, round_completed: completed, next_round: null };
  }, [round, stage, localProgress]);
  const next = useCallback(() => {
    if (finished.current) return;
    // Snapshot the child's accepted log once; a failed clip retry never re-appends answers.
    accepted.current = logs.current;
    if (stage === round.items.length - 1) {
      finished.current = true;
      // Review tours may place this game first; only the tour's last game submits.
      if (round.next_round && onNextRound) onNextRound(round.next_round);
      else onComplete();
    }
    else setStage((value) => value + 1);
  }, [round, stage, onComplete, onNextRound]);
  const View = step.mechanic === 'letter_hunt' ? LetterHuntRoundView
    : ['syllable_train', 'word_construction'].includes(step.mechanic) ? MemoryTrainRoundView : SoundChoiceRoundView;
  return <Box className={`mixed-final ${stage === round.items.length - 1 ? 'is-last-challenge' : ''}`}>
    <View key={step.id} {...props} round={childRound} localProgress={record} idleHelpDisabled={helpUsed} onComplete={next} />
    {celebrating && <Box className="final-confetti" aria-hidden="true">{Array.from({ length: 24 }, (_, index) =>
      <i key={index} style={{ left: `${(index * 37) % 100}%`, animationDelay: `${(index % 5) * .12}s`,
        backgroundColor: ['#a78bfa','#fbbf24','#4ade80','#60a5fa','#fb7185'][index % 5] }} />)}</Box>}
  </Box>;
}
